/**
 * @author Tomas Frinta
 * @version 1.00 (10.4.2019)
 * 
 * verze 1.01 (13.6.2019)
 * - Metoda reset() zmenena na metodu clear() s moznosti odstraneni polozek pole.
 * 
 * verze: 1.02 (9.7.2019)
 * - foreach() predava callbacku index polozky
 * 
 * verze: 1.03 (5.2.2020)
 * - null nahrazeno undefined
 */


export class WrappedArray<T> {
    private _array: T[];

    private _firstItemId: number;
    private _size: number;

    /**
    * (RO) Celkovy pocet polozek v poli
    */
    get size(): number { return this._size; }

    /**
     * (RO) True, pokud je pole prazdne
     */
    get empty(): boolean { return this._size == 0; }

    /**
     * (RO) Prvni polozka v poli nebo undefined, pokud je pole prazdne
     */
    get first(): T { return this.getItemAtIndex(0); }

    /**
     * (RO) Posledni polozka v poli nebo undefined, pokud je pole prazdne
     */
    get last(): T { return this.getItemAtIndex(this._size - 1); }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this._array = [];

        this._firstItemId = 0;
        this._size = 0;
    }

    //-------------------------------------------------------------------------
    /**
     * Pridani nove polozky do pole
     * @param item Polozka, kterou chceme pridat
     * @param atTheEnd True, pokud chceme polozku pridat jako polsedni polozku. False pokud jako prvni polozku. (vychozi hodnota je true)
     */
    //-------------------------------------------------------------------------
    add(item: T, atTheEnd: boolean = true): T {
        // Zajistit misto pro novou polozku
        this.ensureSpaceForNewItem(atTheEnd);

        let itemId: number;

        if (atTheEnd) {
            // Pridat polozku za posledni existujici
            itemId = this.itemId2ItemArrayPos(this._size);
        }
        else {
            // Pridat polozku pred prvni existujici
            if (this._size != 0) {
                itemId = this._firstItemId - 1;

                if (itemId < 0)
                    itemId = this._array.length - 1;
            }
            else {
                itemId = 0;
            }

            this._firstItemId = itemId;
        }

        this._array[itemId] = item;
        this._size++;

        return item;
    }

    //-------------------------------------------------------------------------
    /**
     * Odstraneni polozky z pole
     * @param atTheEnd True, pokud se ma odstranit posledni polozka. False, pokud se ma odstranit prvni polozka. (vychozi hodnota je true)
     */
    //-------------------------------------------------------------------------
    remove(atTheEnd: boolean = true): T {
        let item: T;

        if (this._size != 0) {
            if (atTheEnd) {
                // Odstranit posledni poozku
                let itemId = this.itemId2ItemArrayPos(this._size - 1)

                item = this._array[itemId];
                this._array[itemId] = undefined;
            }
            else {
                // Odstranit prvni polozku
                item = this._array[this._firstItemId];
                this._array[this._firstItemId] = undefined;

                if (++this._firstItemId == this._array.length)
                    this._firstItemId = 0;
            }

            this._size--;
        }

        return item;
    }

    //-------------------------------------------------------------------------
    /**
     * Vyprazdneni pole
     * @param removeItems Pokud true, tak vsechny polozky pole nastavi na undefied. Pokud false, tak pouze resetuje velikost pole na 0. (default = true)
     */
    //-------------------------------------------------------------------------
    clear(removeItems: boolean = true): void {
        if (removeItems) {
            let itemCnt = this._size;
            let itemId = this._firstItemId;

            while (itemCnt-- != 0) {
                this._array[itemId] = undefined;

                if (++itemId == this._array.length)
                    itemId = 0;
            }
        }

        this._firstItemId = 0;
        this._size = 0;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    getItemAtIndex(itemId: number): T {
        if (itemId >= this._size)
            return undefined;

        return this._array[this.itemId2ItemArrayPos(itemId)];
    }

    //-------------------------------------------------------------------------
    /**
     * Vola zadanou callback funkci pro kazdou polozku pole.
     * @param callback
     * @param context
     */
    //-------------------------------------------------------------------------
    forEach(callback: (n: T, index: number) => void, context?: any): void {
        let remItemCnt = this._size;

        if (remItemCnt == 0)
            return;

        let itemId = this._firstItemId;

        while (remItemCnt-- != 0) {
            callback.call(context, this._array[itemId], itemId);

            if (++itemId == this._array.length)
                itemId = 0;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vola zadanou callback funkci pro kazdou polozku pole. Volani se prerusi, pokud callback funkce vrati false.
     * @param callback
     * @param context
     */
    //-------------------------------------------------------------------------
    every(callback: (n: T) => boolean, context?: any): void {
        let remItemCnt = this._size;

        if (remItemCnt == 0)
            return;

        let itemId = this._firstItemId;

        while (remItemCnt-- != 0) {
            if (!callback.call(context, this._array[itemId]))
                break;

            if (++itemId == this._array.length)
                itemId = 0;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Zajistit misto pro novou polozku na konci nebo na zacatku pole
     * @param atTheEnd True, pokud se ma misto pro polozku zajistit za posledni polozkou pole
     */
    //-------------------------------------------------------------------------
    private ensureSpaceForNewItem(atTheEnd: boolean): void {
        if (this._size < this._array.length)
            // V poli jeste je misto
            return;

        if (this._size != 0) {
            // V poli jiz neni misto a obsahuje polozky
            if (this._firstItemId != 0) {
                // Zajistit, aby polozky zacinali na pozici 0
                this._array = this.toArray();
                this._firstItemId = 0;
            }

            if (!atTheEnd) {
                // Pripravit misto pro polozku na zacatku pole
                this._array.unshift(undefined);
                this._firstItemId = 1;

                return;
            }
        }

        // Pripravit misto pro polozku na konci pole
        this._array.push(undefined);
    }

    //-------------------------------------------------------------------------
    /**
     * Prekopiruje polozky ze stareho pole do noveho. Index prvni polozku bude 0. Velikosti noveho pole bude rovna poctu polozek v poli, takze nove pole muze byt mensi nez puvodni.
     */
    //-------------------------------------------------------------------------
    private toArray(): T[] {
        let newArray: T[] = [];
        let oldArray = this._array;

        let itemCnt = this._size;
        let itemId = this._firstItemId;

        while (itemCnt-- != 0) {
            newArray.push(oldArray[itemId]);

            if (++itemId == this._array.length)
                itemId = 0;
        }

        return newArray;
    }

    //-------------------------------------------------------------------------
    /**
     * Prevod indexu polozky na pozici polozky v poli
     * @param itemId
     */
    //-------------------------------------------------------------------------
    private itemId2ItemArrayPos(itemId: number): number {
        itemId += this._firstItemId;

        if (itemId >= this._array.length)
            itemId -= this._array.length;

        return itemId;
    }
}